import DownloadIcon from '@mui/icons-material/GetApp'
import { Box, Card, CardContent, Grid, IconButton, Tooltip, Typography } from '@mui/material'
import countries from 'i18n-iso-countries'
import en from 'i18n-iso-countries/langs/en.json'
import { get, isEmpty } from 'lodash'
import { FunctionField, Loading, SimpleShowLayout, Title, TopToolbar, useGetIdentity } from 'react-admin'

import AdvancedTextField from '../../../components/AdvancedTextField'
import ShowCardHeader from '../../../components/ShowCardHeader'
import {
  PAYOUT_BANK_ACCOUNT_STATUSES_COLORS,
  PAYOUT_BANK_ACCOUNT_STATUSES_ICONS,
  PAYOUT_BANK_ACCOUNT_STATUSES_LABELS,
  PAYOUT_REPORT_STATUS_GENERATED,
  PAYOUT_REPORT_STATUSES_COLORS,
  PAYOUT_REPORT_STATUSES_ICONS,
  PAYOUT_REPORT_STATUSES_LABELS,
  PAYOUT_SETUP_STATUS_COMPLETED,
} from '../../../config/payout'
import { SYSTEM_PERMISSION_READ } from '../../../config/permissions'
import { useCommonStyles } from '../../../config/theme'
import { useResourcePermissions } from '../../../domain/permissions'
import { formatDate, formatDateTime, parseDateAsDateTime } from '../../../utils/dates'
import { useSmallScreen } from '../../../utils/theme'
import { CardTitle } from '../../common'
import { useShowStyles } from '../../common/show'
import config, { PAYOUT_BASE_PATH, PAYOUT_TITLE } from '../config'
import { useGetPayoutReports, useGetPayoutSellerAccount, useGetPayoutSettings, useGetPayoutSetupStatus } from '../hooks'
import { payoutIconColorStyles, payoutShowStyles } from '../styles'

countries.registerLocale(en)

const LegalEntityAddressField = (props) => {
  const commonClasses = useCommonStyles()
  return (
    <FunctionField
      {...props}
      sx={commonClasses.fullWidth}
      render={(record, source) => {
        const address = get(record, source)
        return `${address?.street} ${address?.houseNumber}, ${address?.zipcode} ${address?.city}, ${countries.getName(
          address?.country,
          'en',
        )}`
      }}
    />
  )
}
LegalEntityAddressField.defaultProps = {
  source: 'address',
  label: 'Address',
}

const SetupCompletedView = () => {
  const sellerAccount = useGetPayoutSellerAccount()
  const settings = useGetPayoutSettings()
  const reports = useGetPayoutReports()

  const { identity } = useGetIdentity()
  const isSmallScreen = useSmallScreen()
  const commonClasses = useCommonStyles()
  const showClasses = useShowStyles()

  if (!sellerAccount || !settings || !identity) {
    return <Loading sx={payoutShowStyles.loadingContainer} />
  }

  return (
    <>
      <Card sx={payoutShowStyles.card}>
        <ShowCardHeader title={PAYOUT_TITLE} />
      </Card>
      <Grid container direction={isSmallScreen ? 'column-reverse' : 'row'} spacing={1}>
        <Grid item size={8}>
          <Card sx={payoutShowStyles.card} data-testid="bankAccountSection">
            <CardContent sx={showClasses.subTitleContainer}>
              <CardTitle text="Bank accounts" size={16} />
            </CardContent>
            <CardContent sx={commonClasses.borderTop}>
              {isEmpty(sellerAccount.externalAccounts) ? (
                <Typography variant="body2">No bank account</Typography>
              ) : (
                sellerAccount.externalAccounts
                  .sort((a, b) => a.weight - b.weight)
                  .map((account, index) => {
                    const isPrimary = index === 0
                    const accountStatus = account.kycStatus.toUpperCase()
                    const StatusIcon = PAYOUT_BANK_ACCOUNT_STATUSES_ICONS[accountStatus]
                    return (
                      <Box
                        sx={{
                          pr: 1,
                          ...payoutShowStyles.listItemContainer,
                          ...commonClasses.flexRowCenterContainer,
                          ...(isPrimary ? 'solid' : 'dashed'),
                        }}
                        key={account.code}
                      >
                        <div style={{ ...commonClasses.flexRowCenterContainer, ...commonClasses.halfWidth }}>
                          <Tooltip title={PAYOUT_BANK_ACCOUNT_STATUSES_LABELS[accountStatus]} placement="right">
                            <StatusIcon
                              sx={{
                                color: payoutIconColorStyles[PAYOUT_BANK_ACCOUNT_STATUSES_COLORS[accountStatus]],
                                ...payoutShowStyles.statusIcon,
                              }}
                            />
                          </Tooltip>
                          <Typography variant="body2">
                            {account.accountNumber
                              .replace(/[^\dA-Z]/g, '')
                              .replace(/(.{4})/g, '$1 ')
                              .trim()}
                          </Typography>
                        </div>
                        <div
                          style={{
                            justifyContent: 'space-between',
                            marginLeft: isSmallScreen ? 8 : 0,
                            ...commonClasses.flexRowCenterContainer,
                            ...commonClasses.halfWidth,
                          }}
                        >
                          <Typography variant="body2" sx={{ mr: 1 }}>
                            {account.displayName}
                          </Typography>
                        </div>
                      </Box>
                    )
                  })
              )}
            </CardContent>
          </Card>
          <Card data-testid="reportsSection">
            <CardContent sx={showClasses.subTitleContainer}>
              <CardTitle text="Reports" size={16} />
            </CardContent>
            <CardContent sx={commonClasses.borderTop}>
              {isEmpty(reports) ? (
                <Typography variant="body2">No reports</Typography>
              ) : (
                reports
                  .sort((a, b) => parseDateAsDateTime(b.createdAt) - parseDateAsDateTime(a.createdAt))
                  .map((report) => {
                    const reportStatus = report.status.toUpperCase()
                    const isGenerated = reportStatus === PAYOUT_REPORT_STATUS_GENERATED
                    const StatusIcon = PAYOUT_REPORT_STATUSES_ICONS[reportStatus]
                    return (
                      <Box
                        sx={{
                          ...payoutShowStyles.listItemContainer,
                          ...commonClasses.flexRowCenterContainer,
                          ...commonClasses.solidBorder,
                        }}
                        key={report.id}
                      >
                        <div
                          style={{
                            ...commonClasses.flexRowCenterContainer,
                            ...(isSmallScreen ? commonClasses.spacer : commonClasses.halfWidth),
                          }}
                        >
                          <Tooltip title={PAYOUT_REPORT_STATUSES_LABELS[reportStatus]} placement="right">
                            <StatusIcon
                              sx={{
                                color: payoutIconColorStyles[PAYOUT_REPORT_STATUSES_COLORS[reportStatus]],
                                ...payoutShowStyles.statusIcon,
                              }}
                            />
                          </Tooltip>
                          <Typography variant="body2">
                            {`New splits until ${
                              Boolean(report.toDate) && report.toDate !== 'null' ? formatDate(report.toDate) : 'n/a'
                            }`}
                          </Typography>
                        </div>
                        <Box
                          sx={{
                            justifyContent: 'space-between',
                            marginLeft: isSmallScreen ? 1 : 0,
                            ...commonClasses.flexRowCenterContainer,
                            ...(isSmallScreen ? {} : commonClasses.halfWidth),
                          }}
                        >
                          <Typography variant="body2">
                            {isSmallScreen
                              ? null
                              : Boolean(report.createdAt) && report.createdAt !== 'null'
                                ? `Created on ${formatDateTime(report.createdAt)}`
                                : 'No creation date'}
                          </Typography>
                          <Tooltip title="Download report" placement="left">
                            <IconButton
                              sx={{ color: 'primary.main' }}
                              disabled={!isGenerated || report.url === 'null'}
                              href={report.url}
                              size="large"
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    )
                  })
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item size={4}>
          <Card data-testid="companyInfoSection">
            <CardContent sx={showClasses.subTitleContainer}>
              <CardTitle text="Company info" size={16} />
            </CardContent>
            <SimpleShowLayout record={sellerAccount.legalEntity} sx={showClasses.fieldContainer}>
              <AdvancedTextField source="legalName" label="Legal name" sx={commonClasses.fullWidth} />
              <AdvancedTextField source="registrationNumber" label="Registration number" sx={commonClasses.fullWidth} />
              <AdvancedTextField source="taxNumber" label="VAT number" sx={commonClasses.fullWidth} />
              <LegalEntityAddressField />
              <AdvancedTextField source="phone" label="Phone number" sx={commonClasses.fullWidth} />
              <AdvancedTextField source="email" label="Email address" sx={commonClasses.fullWidth} />
            </SimpleShowLayout>
          </Card>
        </Grid>
      </Grid>
    </>
  )
}

export const PayoutShowScreen = () => {
  const [hasRead] = useResourcePermissions(config.name, SYSTEM_PERMISSION_READ)
  const { status: payoutSetupStatus } = useGetPayoutSetupStatus()
  const isSmallScreen = useSmallScreen()

  return (
    <>
      <Title title={PAYOUT_TITLE} />
      {!isSmallScreen && <TopToolbar></TopToolbar>}
      {hasRead && payoutSetupStatus === PAYOUT_SETUP_STATUS_COMPLETED && <SetupCompletedView />}
    </>
  )
}

PayoutShowScreen.path = PAYOUT_BASE_PATH
